import { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import {
  Plus,
  Trash2,
  Pencil,
  CheckCircle2,
  XCircle,
  AlarmClock,
  CalendarClock,
  Calculator,
  CalendarDays,
  CalendarOff,
  UserCheck,
  UserX,
  Clock4,
  ShieldAlert,
  Inbox,
  X,
  Paperclip,
  Sparkles,
  ExternalLink,
  Loader2,
  FileWarning,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AuthImage from "@/components/AuthImage";
import WarningsTab from "@/components/WarningsPanel";
import TimesheetTab from "@/components/TimesheetTab";
import AttachmentPreview from "@/components/AttachmentPreview";
import { openAuthFile } from "@/lib/files";

const PUNCH_LABEL = {
  entry: "Entrada",
  break_start: "Saída intervalo",
  break_end: "Volta intervalo",
  exit: "Saída",
};
const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const STATUS_BADGE = {
  valid: ["bg-emerald-50 text-emerald-800 border-emerald-200", "Válido"],
  pending_review: ["bg-amber-50 text-amber-800 border-amber-200", "Em revisão"],
  rejected: ["bg-rose-50 text-rose-800 border-rose-200", "Rejeitado"],
};
const FACE_BADGE = {
  approved: ["bg-emerald-50 text-emerald-800 border-emerald-200", "Confere"],
  rejected: ["bg-rose-50 text-rose-800 border-rose-200", "Não confere"],
  no_baseline: ["bg-stone-100 text-stone-600 border-stone-200", "Sem cadastro"],
  error: ["bg-amber-50 text-amber-800 border-amber-200", "Erro IA"],
};
const LEAVE_TYPES = [
  { value: "vacation", label: "Férias" },
  { value: "medical", label: "Atestado médico" },
  { value: "license", label: "Licença" },
  { value: "comp_day", label: "Folga compensatória" },
  { value: "other", label: "Outro" },
];
const LEAVE_LABEL = Object.fromEntries(LEAVE_TYPES.map((t) => [t.value, t.label]));

const todayStr = () => new Date().toLocaleDateString("sv-SE");
const emptySched = {
  name: "",
  entry_time: "08:00",
  break_start: "12:00",
  break_end: "13:00",
  exit_time: "17:00",
  tolerance_minutes: 10,
  overtime_policy: "bank",
  workdays: [0, 1, 2, 3, 4],
  user_ids: [],
};
const emptyManual = { user_id: "", local_date: todayStr(), type: "entry", time: "", reason: "" };
const emptyLeave = { user_id: "", type: "vacation", date_from: "", date_to: "", note: "" };

const Kpi = ({ icon: Icon, label, value, tone, testId }) => (
  <Card className="rounded-xl border-stone-200 bg-white p-3 flex items-center gap-3">
    <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${tone}`}>
      <Icon className="h-4.5 w-4.5 h-5 w-5" />
    </div>
    <div>
      <div className="font-display text-xl font-extrabold leading-none" data-testid={testId}>
        {value ?? "—"}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-stone-500 font-body mt-0.5">
        {label}
      </div>
    </div>
  </Card>
);

export default function TimeClockAdmin() {
  const [records, setRecords] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [overview, setOverview] = useState(null);
  const [date, setDate] = useState(todayStr());
  const [statusFilter, setStatusFilter] = useState("all");
  const [schedOpen, setSchedOpen] = useState(false);
  const [editingSched, setEditingSched] = useState(null);
  const [schedForm, setSchedForm] = useState(emptySched);
  const [holidayForm, setHolidayForm] = useState({ date: "", name: "" });
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState(emptyManual);
  const [adjustRec, setAdjustRec] = useState(null);
  const [adjustForm, setAdjustForm] = useState({ time: "", reason: "" });
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveForm, setLeaveForm] = useState(emptyLeave);
  const [requests, setRequests] = useState([]);
  const [reqStatusFilter, setReqStatusFilter] = useState("pending");

  const loadRequests = useCallback(async () => {
    try {
      const params = {};
      if (reqStatusFilter !== "all") params.status = reqStatusFilter;
      const { data } = await api.get("/timeclock/adjustment-requests", { params });
      setRequests(data);
    } catch (err) {
      console.error("requests load failed", err);
    }
  }, [reqStatusFilter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const resolveRequest = async (r, action) => {
    try {
      await api.patch(`/timeclock/adjustment-requests/${r.id}/resolve`, { action, note: "" });
      if (action === "approve") {
        toast.success(
          r.kind === "absence"
            ? "Justificativa aprovada — afastamento lançado"
            : "Solicitação aprovada — batida aplicada"
        );
      } else {
        toast.success("Solicitação recusada");
      }
      loadRequests();
      loadRecords();
      loadOverview();
      if (r.kind === "absence") loadBase();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao resolver solicitação");
    }
  };

  // Fase F — request detail + AI atestado analysis (archived on the request)
  const [reqDetail, setReqDetail] = useState(null);
  const [reqAnalysis, setReqAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeMsg, setAnalyzeMsg] = useState("");

  const openReqDetail = async (r) => {
    setReqDetail(r);
    setReqAnalysis(r.ai_analysis || null);
    setAnalyzeMsg("");
    if (!r.ai_analysis && r.attachment_url) {
      setAnalyzing(true);
      try {
        const { data } = await api.post(`/timeclock/adjustment-requests/${r.id}/analyze`);
        setReqAnalysis(data.analysis);
        setRequests((list) =>
          list.map((x) => (x.id === r.id ? { ...x, ai_analysis: data.analysis } : x))
        );
      } catch (err) {
        setAnalyzeMsg(err?.response?.data?.detail || "Falha na análise por IA");
      } finally {
        setAnalyzing(false);
      }
    }
  };

  const loadRecords = useCallback(async () => {
    try {
      const params = { date_from: date, date_to: date };
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/timeclock/records", { params });
      setRecords(data);
    } catch (err) {
      console.error("records load failed", err);
    }
  }, [date, statusFilter]);

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await api.get("/timeclock/overview");
      setOverview(data);
    } catch (err) {
      console.error("overview load failed", err);
    }
  }, []);

  const loadBase = useCallback(async () => {
    try {
      const [{ data: s }, { data: u }, { data: h }, { data: l }, { data: un }] = await Promise.all([
        api.get("/timeclock/schedules"),
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/timeclock/holidays").catch(() => ({ data: [] })),
        api.get("/timeclock/leaves").catch(() => ({ data: [] })),
        api.get("/units").catch(() => ({ data: [] })),
      ]);
      setSchedules(s);
      setUsers(u || []);
      setHolidays(h || []);
      setLeaves(l || []);
      setUnits(un || []);
    } catch (err) {
      console.error("base load failed", err);
    }
  }, []);

  useEffect(() => {
    loadBase();
    loadOverview();
  }, [loadBase, loadOverview]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const userName = useMemo(() => {
    const m = {};
    for (const u of users) m[u.id] = u.name;
    return m;
  }, [users]);

  const unitName = useMemo(() => {
    const m = {};
    for (const u of units) m[u.id] = u.name;
    return m;
  }, [units]);

  const review = async (id, action) => {
    try {
      await api.patch(`/timeclock/records/${id}/review`, { action, note: "" });
      toast.success(action === "approve" ? "Registro aprovado" : "Registro rejeitado");
      loadRecords();
      loadOverview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao revisar");
    }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    try {
      await api.post("/timeclock/records/manual", manualForm);
      toast.success("Batida manual incluída");
      setManualOpen(false);
      setManualForm(emptyManual);
      loadRecords();
      loadOverview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao incluir batida");
    }
  };

  const openAdjust = (r) => {
    setAdjustRec(r);
    setAdjustForm({
      time: new Date(r.timestamp).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
      reason: "",
    });
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/timeclock/records/${adjustRec.id}/adjust`, adjustForm);
      toast.success("Batida ajustada — original mantida no histórico");
      setAdjustRec(null);
      loadRecords();
      loadOverview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao ajustar");
    }
  };

  const submitLeave = async (e) => {
    e.preventDefault();
    try {
      await api.post("/timeclock/leaves", leaveForm);
      toast.success("Afastamento cadastrado");
      setLeaveOpen(false);
      setLeaveForm(emptyLeave);
      loadBase();
      loadOverview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao cadastrar afastamento");
    }
  };

  const removeLeave = async (id) => {
    try {
      await api.delete(`/timeclock/leaves/${id}`);
      toast.success("Afastamento removido");
      loadBase();
      loadOverview();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao remover");
    }
  };

  const openSchedCreate = () => {
    setEditingSched(null);
    setSchedForm(emptySched);
    setSchedOpen(true);
  };

  const openSchedEdit = (s) => {
    setEditingSched(s);
    setSchedForm({
      name: s.name,
      unit_id: s.unit_id || "all",
      entry_time: s.entry_time,
      break_start: s.break_start,
      break_end: s.break_end,
      exit_time: s.exit_time,
      tolerance_minutes: s.tolerance_minutes,
      overtime_policy: s.overtime_policy || "bank",
      workdays: s.workdays || [0, 1, 2, 3, 4],
      user_ids: s.user_ids || [],
    });
    setSchedOpen(true);
  };

  const setS = (k, v) => setSchedForm((f) => ({ ...f, [k]: v }));

  const toggleWorkday = (i) =>
    setSchedForm((f) => {
      const cur = new Set(f.workdays);
      if (cur.has(i)) cur.delete(i);
      else cur.add(i);
      return { ...f, workdays: Array.from(cur).sort() };
    });

  const toggleUser = (uid) =>
    setSchedForm((f) => {
      const cur = new Set(f.user_ids);
      if (cur.has(uid)) cur.delete(uid);
      else cur.add(uid);
      return { ...f, user_ids: Array.from(cur) };
    });

  const submitSched = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...schedForm,
        tolerance_minutes: Number(schedForm.tolerance_minutes) || 0,
        unit_id: schedForm.unit_id === "all" ? null : schedForm.unit_id,
      };
      if (editingSched) {
        await api.patch(`/timeclock/schedules/${editingSched.id}`, payload);
        toast.success("Jornada atualizada");
      } else {
        await api.post("/timeclock/schedules", payload);
        toast.success("Jornada criada");
      }
      setSchedOpen(false);
      loadBase();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar jornada");
    }
  };

  const removeSched = async (id) => {
    if (!confirm("Excluir esta jornada?")) return;
    try {
      await api.delete(`/timeclock/schedules/${id}`);
      toast.success("Jornada removida");
      loadBase();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao excluir");
    }
  };

  const addHoliday = async (e) => {
    e.preventDefault();
    try {
      await api.post("/timeclock/holidays", holidayForm);
      toast.success("Feriado cadastrado");
      setHolidayForm({ date: "", name: "" });
      loadBase();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao cadastrar feriado");
    }
  };

  const removeHoliday = async (id) => {
    try {
      await api.delete(`/timeclock/holidays/${id}`);
      toast.success("Feriado removido");
      loadBase();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao remover");
    }
  };

  const timeOf = (iso) =>
    new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  return (
    <Layout title="Gestão de Ponto" subtitle="Registros, apuração, jornadas, feriados e afastamentos">
      {/* Fase C — HR overview KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5" data-testid="timeclock-overview">
        <Kpi icon={CalendarClock} label="Escalados hoje" value={overview?.expected} tone="bg-stone-100 text-stone-700" testId="overview-expected" />
        <Kpi icon={UserCheck} label="Presentes" value={overview?.present} tone="bg-emerald-100 text-emerald-800" testId="overview-present" />
        <Kpi icon={Clock4} label="Atrasados" value={overview?.late} tone="bg-amber-100 text-amber-800" testId="overview-late" />
        <Kpi icon={UserX} label="Sem registro" value={overview?.missing} tone="bg-rose-100 text-rose-800" testId="overview-missing" />
        <Kpi icon={ShieldAlert} label="Em revisão" value={overview?.pending_review} tone="bg-amber-100 text-amber-800" testId="overview-pending" />
        <Kpi icon={CalendarOff} label="Afastados" value={overview?.on_leave} tone="bg-indigo-100 text-indigo-800" testId="overview-on-leave" />
      </div>

      <Tabs defaultValue="records" data-testid="timeclock-admin-page">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="records" data-testid="timeclock-tab-records">
            <AlarmClock className="h-4 w-4 mr-1.5" /> Registros
          </TabsTrigger>
          <TabsTrigger value="timesheet" data-testid="timeclock-tab-timesheet">
            <Calculator className="h-4 w-4 mr-1.5" /> Apuração
          </TabsTrigger>
          <TabsTrigger value="schedules" data-testid="timeclock-tab-schedules">
            <CalendarClock className="h-4 w-4 mr-1.5" /> Jornadas
          </TabsTrigger>
          <TabsTrigger value="holidays" data-testid="timeclock-tab-holidays">
            <CalendarDays className="h-4 w-4 mr-1.5" /> Feriados
          </TabsTrigger>
          <TabsTrigger value="leaves" data-testid="timeclock-tab-leaves">
            <CalendarOff className="h-4 w-4 mr-1.5" /> Afastamentos
          </TabsTrigger>
          <TabsTrigger value="requests" data-testid="timeclock-tab-requests">
            <Inbox className="h-4 w-4 mr-1.5" /> Solicitações
            {requests.filter((r) => r.status === "pending").length > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-500 text-white text-[10px] px-1.5 py-0.5 leading-none">
                {requests.filter((r) => r.status === "pending").length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="warnings" data-testid="timeclock-tab-warnings">
            <FileWarning className="h-4 w-4 mr-1.5" /> Advertências
          </TabsTrigger>
        </TabsList>

        <TabsContent value="records" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">Data</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1 bg-white w-40"
                data-testid="timeclock-date-filter"
              />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="mt-1 bg-white w-44" data-testid="timeclock-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="valid">Válidos</SelectItem>
                  <SelectItem value="pending_review">Em revisão</SelectItem>
                  <SelectItem value="rejected">Rejeitados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-stone-500"
              onClick={() => {
                setDate(todayStr());
                setStatusFilter("all");
              }}
              data-testid="timeclock-filters-reset"
            >
              <X className="h-3.5 w-3.5 mr-1" /> Limpar
            </Button>
            <div className="flex-1" />
            <Button
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              onClick={() => {
                setManualForm({ ...emptyManual, local_date: date });
                setManualOpen(true);
              }}
              data-testid="manual-punch-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Batida manual
            </Button>
          </div>

          <Card className="rounded-xl border-stone-200 bg-white overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead className="font-body">Colaborador</TableHead>
                  <TableHead className="font-body">Batida</TableHead>
                  <TableHead className="font-body">Selfie</TableHead>
                  <TableHead className="font-body">Face (IA)</TableHead>
                  <TableHead className="font-body">GPS</TableHead>
                  <TableHead className="font-body">Atraso</TableHead>
                  <TableHead className="font-body">Status</TableHead>
                  <TableHead className="font-body text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="timeclock-records-table">
                {records.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-stone-500 py-10">
                      Nenhum registro nesta data.
                    </TableCell>
                  </TableRow>
                )}
                {records.map((r) => {
                  const [stCls, stLabel] = STATUS_BADGE[r.status] || STATUS_BADGE.valid;
                  const [fCls, fLabel] = FACE_BADGE[r.face_match] || FACE_BADGE.error;
                  return (
                    <TableRow key={r.id} data-testid={`timeclock-admin-row-${r.id}`}>
                      <TableCell className="font-display font-semibold text-sm">
                        {r.user_name || userName[r.user_id] || r.user_id.slice(0, 8)}
                      </TableCell>
                      <TableCell className="font-body text-sm">
                        {PUNCH_LABEL[r.type] || r.type}
                        {r.source === "manual" && (
                          <Badge variant="outline" className="ml-1.5 bg-indigo-50 text-indigo-800 border-indigo-200 text-[9px]">
                            Manual
                          </Badge>
                        )}
                        <div className="text-xs text-stone-500 tabular-nums">
                          {timeOf(r.timestamp)}
                          {r.expected_time ? ` (prev. ${r.expected_time})` : ""}
                        </div>
                      </TableCell>
                      <TableCell>
                        {r.selfie_url ? (
                          <AuthImage
                            src={r.selfie_url}
                            alt="selfie"
                            className="h-10 w-10 rounded-md object-cover"
                          />
                        ) : (
                          <span className="text-xs text-stone-400 font-body">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${fCls} text-[10px]`}>
                          {fLabel}
                          {["approved", "rejected"].includes(r.face_match)
                            ? ` ${Math.round((r.face_confidence || 0) * 100)}%`
                            : ""}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {r.latitude == null ? (
                          <Badge variant="outline" className="bg-stone-100 text-stone-600 border-stone-200 text-[10px]">
                            Sem GPS
                          </Badge>
                        ) : r.geo_out_of_range ? (
                          <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 text-[10px]">
                            Fora · {Math.round(r.distance_m || 0)}m
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">
                            No local
                          </Badge>
                        )}
                        {r.mock_suspect && (
                          <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 text-[10px] ml-1">
                            Suspeito
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-body text-sm">
                        {r.late ? (
                          <span className="text-rose-700 font-semibold">+{r.delay_minutes} min</span>
                        ) : (
                          <span className="text-stone-400">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`${stCls} text-[10px]`}>
                          {stLabel}
                        </Badge>
                        {r.review_note && (
                          <div className="text-[10px] text-stone-400 font-body max-w-36 truncate" title={r.review_note}>
                            {r.review_note}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {r.status === "pending_review" && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Aprovar"
                                onClick={() => review(r.id, "approve")}
                                data-testid={`timeclock-approve-${r.id}`}
                              >
                                <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Rejeitar"
                                onClick={() => review(r.id, "reject")}
                                data-testid={`timeclock-reject-${r.id}`}
                              >
                                <XCircle className="h-4 w-4 text-rose-600" />
                              </Button>
                            </>
                          )}
                          {r.status !== "rejected" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Ajustar horário"
                              onClick={() => openAdjust(r)}
                              data-testid={`adjust-record-${r.id}`}
                            >
                              <Pencil className="h-4 w-4 text-stone-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="timesheet" className="mt-4">
          <TimesheetTab />
        </TabsContent>

        <TabsContent value="warnings" className="mt-4">
          <WarningsTab />
        </TabsContent>

        <TabsContent value="schedules" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Button
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              onClick={openSchedCreate}
              data-testid="timeclock-schedule-new"
            >
              <Plus className="h-4 w-4 mr-2" /> Nova jornada
            </Button>
          </div>

          <div className="grid gap-3 md:grid-cols-2" data-testid="timeclock-schedules-list">
            {schedules.length === 0 && (
              <Card className="rounded-xl border-stone-200 bg-white p-6 text-center text-stone-500 font-body md:col-span-2">
                Nenhuma regra de jornada cadastrada. Crie uma para calcular atrasos e tolerâncias.
              </Card>
            )}
            {schedules.map((s) => (
              <Card key={s.id} className="rounded-xl border-stone-200 bg-white p-4" data-testid={`timeclock-schedule-${s.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display font-bold">{s.name}</div>
                    <div className="text-sm text-stone-600 font-body mt-0.5 tabular-nums">
                      {s.entry_time}–{s.break_start} · {s.break_end}–{s.exit_time}
                    </div>
                    <div className="text-xs text-stone-500 font-body mt-1">
                      Tolerância {s.tolerance_minutes} min ·{" "}
                      {(s.workdays || []).map((d) => WEEKDAYS[d]).join(", ")}
                    </div>
                    <div className="text-xs font-body mt-0.5 text-stone-500" data-testid={`schedule-unit-${s.id}`}>
                      Unidade:{" "}
                      <span className="font-semibold text-stone-700">
                        {s.unit_id ? unitName[s.unit_id] || "—" : "Todas as unidades"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${
                          s.overtime_policy === "payroll"
                            ? "bg-sky-50 text-sky-800 border-sky-200"
                            : "bg-emerald-50 text-emerald-800 border-emerald-200"
                        }`}
                      >
                        {s.overtime_policy === "payroll" ? "Extras p/ folha" : "Banco de horas"}
                      </Badge>
                      <span className="text-xs text-emerald-800 font-body">
                        {(s.user_ids || []).length} colaborador(es)
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openSchedEdit(s)} data-testid={`timeclock-schedule-edit-${s.id}`}>
                      <Pencil className="h-4 w-4 text-stone-500" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => removeSched(s.id)} data-testid={`timeclock-schedule-delete-${s.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="holidays" className="mt-4 space-y-4">
          <Card className="rounded-xl border-stone-200 bg-white p-4">
            <div className="font-display font-bold text-sm mb-1">Feriados da empresa</div>
            <p className="text-xs text-stone-500 font-body mb-3">
              Em feriados, trabalho conta como hora extra 100% e a ausência não desconta do banco.
            </p>
            <form onSubmit={addHoliday} className="flex flex-wrap items-end gap-2 mb-4">
              <div>
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={holidayForm.date}
                  onChange={(e) => setHolidayForm((f) => ({ ...f, date: e.target.value }))}
                  required
                  className="mt-1 bg-white w-40"
                  data-testid="holiday-date-input"
                />
              </div>
              <div className="flex-1 min-w-40">
                <Label className="text-xs">Nome</Label>
                <Input
                  value={holidayForm.name}
                  onChange={(e) => setHolidayForm((f) => ({ ...f, name: e.target.value }))}
                  required
                  placeholder="Ex: Natal"
                  className="mt-1 bg-white"
                  data-testid="holiday-name-input"
                />
              </div>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="holiday-add-btn">
                <Plus className="h-4 w-4 mr-1" /> Adicionar
              </Button>
            </form>
            <div className="space-y-1.5" data-testid="holidays-list">
              {holidays.length === 0 && (
                <div className="text-sm text-stone-500 font-body text-center py-4">
                  Nenhum feriado cadastrado.
                </div>
              )}
              {holidays.map((h) => (
                <div
                  key={h.id}
                  className="flex items-center gap-3 border border-stone-200 rounded-md px-3 py-2"
                  data-testid={`holiday-row-${h.id}`}
                >
                  <CalendarDays className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
                  <span className="font-body text-sm tabular-nums">
                    {h.date.split("-").reverse().join("/")}
                  </span>
                  <span className="flex-1 font-body text-sm font-semibold">{h.name}</span>
                  <button
                    type="button"
                    onClick={() => removeHoliday(h.id)}
                    className="text-stone-400 hover:text-rose-600"
                    data-testid={`holiday-delete-${h.id}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="leaves" className="mt-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-stone-500 font-body">
              Dias em férias/afastamento não contam falta nem débito no banco de horas.
            </p>
            <Button
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              onClick={() => {
                setLeaveForm(emptyLeave);
                setLeaveOpen(true);
              }}
              data-testid="leave-new-btn"
            >
              <Plus className="h-4 w-4 mr-2" /> Novo afastamento
            </Button>
          </div>
          <div className="space-y-2" data-testid="leaves-list">
            {leaves.length === 0 && (
              <Card className="rounded-xl border-stone-200 bg-white p-6 text-center text-stone-500 font-body">
                Nenhum afastamento cadastrado.
              </Card>
            )}
            {leaves.map((l) => (
              <Card
                key={l.id}
                className="rounded-xl border-stone-200 bg-white px-4 py-3 flex items-center gap-3 flex-wrap"
                data-testid={`leave-row-${l.id}`}
              >
                <CalendarOff className="h-4 w-4 text-indigo-700 shrink-0" />
                <div className="flex-1 min-w-48">
                  <div className="font-display font-semibold text-sm">
                    {l.user_name || userName[l.user_id] || l.user_id.slice(0, 8)}
                  </div>
                  <div className="text-xs text-stone-500 font-body tabular-nums">
                    {l.date_from.split("-").reverse().join("/")} até{" "}
                    {l.date_to.split("-").reverse().join("/")}
                    {l.note ? ` · ${l.note}` : ""}
                  </div>
                </div>
                <Badge variant="outline" className="bg-indigo-50 text-indigo-800 border-indigo-200 text-[10px]">
                  {LEAVE_LABEL[l.type] || l.type}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeLeave(l.id)}
                  data-testid={`leave-delete-${l.id}`}
                >
                  <Trash2 className="h-4 w-4 text-rose-600" />
                </Button>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="requests" className="mt-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-stone-500 font-body">
              Solicitações de ajuste e justificativas de falta enviadas pelos colaboradores no
              espelho de ponto. Aprovar aplica a batida ou lança o afastamento automaticamente
              (com trilha de auditoria).
            </p>
            <Select value={reqStatusFilter} onValueChange={setReqStatusFilter}>
              <SelectTrigger className="bg-white w-44" data-testid="request-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pendentes</SelectItem>
                <SelectItem value="approved">Aprovadas</SelectItem>
                <SelectItem value="rejected">Recusadas</SelectItem>
                <SelectItem value="all">Todas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2" data-testid="requests-list">
            {requests.length === 0 && (
              <Card className="rounded-xl border-stone-200 bg-white p-6 text-center text-stone-500 font-body">
                Nenhuma solicitação {reqStatusFilter === "pending" ? "pendente" : "encontrada"}.
              </Card>
            )}
            {requests.map((r) => (
              <Card
                key={r.id}
                className="rounded-xl border-stone-200 bg-white px-4 py-3 flex items-center gap-3 flex-wrap cursor-pointer hover:bg-stone-50 transition-colors"
                onClick={() => openReqDetail(r)}
                data-testid={`request-row-${r.id}`}
              >
                {r.kind === "absence" ? (
                  <CalendarOff className="h-4 w-4 text-indigo-600 shrink-0" />
                ) : (
                  <Inbox className="h-4 w-4 text-amber-600 shrink-0" />
                )}
                <div className="flex-1 min-w-56">
                  <div className="font-display font-semibold text-sm">
                    {r.user_name || userName[r.user_id] || r.user_id.slice(0, 8)}
                    {r.kind === "absence" && (
                      <Badge
                        variant="outline"
                        className="ml-2 bg-indigo-50 text-indigo-800 border-indigo-200 text-[9px]"
                      >
                        Justificativa de falta · {LEAVE_LABEL[r.leave_type] || "Outro"}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-stone-600 font-body">
                    {r.kind === "absence" ? (
                      <>
                        {r.local_date.split("-").reverse().join("/")}
                        {r.date_to && r.date_to !== r.local_date
                          ? ` até ${r.date_to.split("-").reverse().join("/")}`
                          : ""}
                        {" — "}
                        {r.reason}
                      </>
                    ) : (
                      <>
                        {r.local_date.split("-").reverse().join("/")} · {PUNCH_LABEL[r.type]} às{" "}
                        <span className="font-semibold tabular-nums">{r.requested_time}</span>
                        {" — "}
                        {r.reason}
                      </>
                    )}
                  </div>
                  {r.attachment_url && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openAuthFile(r.attachment_url);
                      }}
                      className="inline-flex items-center gap-1 text-xs text-emerald-800 underline underline-offset-2 mt-0.5"
                      data-testid={`request-attachment-${r.id}`}
                    >
                      <Paperclip className="h-3 w-3" /> Ver anexo (atestado)
                    </button>
                  )}
                </div>
                {r.status === "pending" ? (
                  <div className="flex gap-1.5">
                    <Button
                      size="sm"
                      className="bg-emerald-800 hover:bg-emerald-900 text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        resolveRequest(r, "approve");
                      }}
                      data-testid={`request-approve-${r.id}`}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Aprovar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-white text-rose-700 border-rose-200 hover:bg-rose-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        resolveRequest(r, "reject");
                      }}
                      data-testid={`request-reject-${r.id}`}
                    >
                      <XCircle className="h-4 w-4 mr-1" /> Recusar
                    </Button>
                  </div>
                ) : (
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      r.status === "approved"
                        ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                        : "bg-rose-50 text-rose-800 border-rose-200"
                    }`}
                  >
                    {r.status === "approved" ? "Aprovada" : "Recusada"}
                  </Badge>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Manual punch dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Incluir batida manual</DialogTitle>
            <DialogDescription className="font-body text-xs">
              A batida entra como registro manual, com autor e motivo na trilha de auditoria.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitManual} className="space-y-3">
            <div>
              <Label>Colaborador *</Label>
              <Select
                value={manualForm.user_id}
                onValueChange={(v) => setManualForm((f) => ({ ...f, user_id: v }))}
              >
                <SelectTrigger className="mt-1 bg-white" data-testid="manual-user-select">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Data</Label>
                <Input
                  type="date"
                  value={manualForm.local_date}
                  onChange={(e) => setManualForm((f) => ({ ...f, local_date: e.target.value }))}
                  required
                  className="mt-1 bg-white"
                  data-testid="manual-date-input"
                />
              </div>
              <div>
                <Label>Batida</Label>
                <Select
                  value={manualForm.type}
                  onValueChange={(v) => setManualForm((f) => ({ ...f, type: v }))}
                >
                  <SelectTrigger className="mt-1 bg-white" data-testid="manual-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PUNCH_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Horário</Label>
                <Input
                  type="time"
                  value={manualForm.time}
                  onChange={(e) => setManualForm((f) => ({ ...f, time: e.target.value }))}
                  required
                  className="mt-1 bg-white"
                  data-testid="manual-time-input"
                />
              </div>
            </div>
            <div>
              <Label>Motivo *</Label>
              <Input
                value={manualForm.reason}
                onChange={(e) => setManualForm((f) => ({ ...f, reason: e.target.value }))}
                required
                placeholder="Ex: colaborador esqueceu de bater"
                className="mt-1 bg-white"
                data-testid="manual-reason-input"
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={!manualForm.user_id}
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid="manual-save-btn"
              >
                Incluir batida
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Adjust punch dialog */}
      <Dialog open={!!adjustRec} onOpenChange={(o) => !o && setAdjustRec(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              Ajustar batida — {adjustRec ? PUNCH_LABEL[adjustRec.type] : ""}
            </DialogTitle>
            <DialogDescription className="font-body text-xs">
              {adjustRec &&
                `${adjustRec.user_name} · ${adjustRec.local_date.split("-").reverse().join("/")} às ${timeOf(
                  adjustRec.timestamp
                )}. A batida original fica rejeitada no histórico e o ajuste entra como registro manual.`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAdjust} className="space-y-3">
            <div>
              <Label>Novo horário *</Label>
              <Input
                type="time"
                value={adjustForm.time}
                onChange={(e) => setAdjustForm((f) => ({ ...f, time: e.target.value }))}
                required
                className="mt-1 bg-white w-36"
                data-testid="adjust-time-input"
              />
            </div>
            <div>
              <Label>Motivo *</Label>
              <Input
                value={adjustForm.reason}
                onChange={(e) => setAdjustForm((f) => ({ ...f, reason: e.target.value }))}
                required
                placeholder="Ex: marcação com horário errado"
                className="mt-1 bg-white"
                data-testid="adjust-reason-input"
              />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="adjust-save-btn">
                Ajustar batida
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Leave dialog */}
      <Dialog
        open={leaveOpen}
        onOpenChange={(o) => {
          setLeaveOpen(o);
          if (!o) setLeaveForm(emptyLeave);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Novo afastamento</DialogTitle>
            <DialogDescription className="font-body text-xs">
              Férias, atestados e licenças. O período não conta falta na apuração.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitLeave} className="space-y-3">
            <div>
              <Label>Colaborador *</Label>
              <Select
                value={leaveForm.user_id}
                onValueChange={(v) => setLeaveForm((f) => ({ ...f, user_id: v }))}
              >
                <SelectTrigger className="mt-1 bg-white" data-testid="leave-user-select">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo</Label>
              <Select
                value={leaveForm.type}
                onValueChange={(v) => setLeaveForm((f) => ({ ...f, type: v }))}
              >
                <SelectTrigger className="mt-1 bg-white" data-testid="leave-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>De *</Label>
                <Input
                  type="date"
                  value={leaveForm.date_from}
                  onChange={(e) => setLeaveForm((f) => ({ ...f, date_from: e.target.value }))}
                  required
                  className="mt-1 bg-white"
                  data-testid="leave-from-input"
                />
              </div>
              <div>
                <Label>Até *</Label>
                <Input
                  type="date"
                  value={leaveForm.date_to}
                  onChange={(e) => setLeaveForm((f) => ({ ...f, date_to: e.target.value }))}
                  required
                  className="mt-1 bg-white"
                  data-testid="leave-to-input"
                />
              </div>
            </div>
            <div>
              <Label>Observação</Label>
              <Input
                value={leaveForm.note}
                onChange={(e) => setLeaveForm((f) => ({ ...f, note: e.target.value }))}
                placeholder="Opcional"
                className="mt-1 bg-white"
                data-testid="leave-note-input"
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={!leaveForm.user_id}
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid="leave-save-btn"
              >
                Cadastrar afastamento
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Fase F — Request detail + AI atestado analysis */}
      <Dialog
        open={!!reqDetail}
        onOpenChange={(o) => {
          if (!o) {
            setReqDetail(null);
            setReqAnalysis(null);
            setAnalyzeMsg("");
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="request-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {reqDetail?.kind === "absence" ? "Justificativa de falta" : "Solicitação de ajuste"} —{" "}
              {reqDetail?.user_name}
            </DialogTitle>
            <DialogDescription className="font-body text-xs">
              Analise o conteúdo antes de aprovar. O atestado e a leitura por IA ficam arquivados
              na solicitação.
            </DialogDescription>
          </DialogHeader>
          {reqDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm font-body">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-stone-500">Período</div>
                  <div className="font-semibold tabular-nums" data-testid="request-detail-period">
                    {reqDetail.local_date.split("-").reverse().join("/")}
                    {reqDetail.date_to && reqDetail.date_to !== reqDetail.local_date
                      ? ` até ${reqDetail.date_to.split("-").reverse().join("/")}`
                      : ""}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-stone-500">Tipo</div>
                  <div className="font-semibold">
                    {reqDetail.kind === "absence"
                      ? LEAVE_LABEL[reqDetail.leave_type] || "Outro"
                      : `${PUNCH_LABEL[reqDetail.type]} às ${reqDetail.requested_time}`}
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wide text-stone-500">Motivo</div>
                  <div data-testid="request-detail-reason">{reqDetail.reason}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-stone-500">Status</div>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      reqDetail.status === "pending"
                        ? "bg-amber-50 text-amber-800 border-amber-200"
                        : reqDetail.status === "approved"
                        ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                        : "bg-rose-50 text-rose-800 border-rose-200"
                    }`}
                  >
                    {reqDetail.status === "pending"
                      ? "Pendente"
                      : reqDetail.status === "approved"
                      ? "Aprovada"
                      : "Recusada"}
                  </Badge>
                </div>
              </div>

              {reqDetail.attachment_url ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-wide text-stone-500 font-body">
                      Atestado / comprovante (arquivado)
                    </div>
                    <button
                      type="button"
                      onClick={() => openAuthFile(reqDetail.attachment_url)}
                      className="inline-flex items-center gap-1 text-xs text-emerald-800 underline underline-offset-2"
                      data-testid="request-detail-open-attachment"
                    >
                      <ExternalLink className="h-3 w-3" /> Abrir em nova aba
                    </button>
                  </div>
                  <AttachmentPreview url={reqDetail.attachment_url} />
                </div>
              ) : (
                <div className="text-xs text-stone-400 font-body">Sem anexo nesta solicitação.</div>
              )}

              {reqDetail.attachment_url && (
                <div
                  className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2"
                  data-testid="request-ai-analysis"
                >
                  <div className="flex items-center gap-1.5 font-display font-bold text-sm text-indigo-900">
                    <Sparkles className="h-4 w-4" /> Leitura do atestado por IA
                  </div>
                  {analyzing ? (
                    <div className="flex items-center gap-2 text-sm text-stone-500 font-body py-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Analisando atestado com IA…
                    </div>
                  ) : reqAnalysis ? (
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-sm font-body">
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-stone-500">
                          Dias de afastamento
                        </div>
                        <div className="font-display font-bold" data-testid="ai-days-off">
                          {reqAnalysis.days_off != null ? `${reqAnalysis.days_off} dia(s)` : "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-stone-500">CID</div>
                        <div className="font-display font-bold" data-testid="ai-cid">
                          {reqAnalysis.cid_code || "—"}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-[10px] uppercase tracking-wide text-stone-500">
                          Período indicado
                        </div>
                        <div className="tabular-nums">
                          {reqAnalysis.date_from
                            ? reqAnalysis.date_from.split("-").reverse().join("/")
                            : "—"}
                          {reqAnalysis.date_to && reqAnalysis.date_to !== reqAnalysis.date_from
                            ? ` até ${reqAnalysis.date_to.split("-").reverse().join("/")}`
                            : ""}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-[10px] uppercase tracking-wide text-stone-500">
                          Resumo
                        </div>
                        <div data-testid="ai-summary">{reqAnalysis.reason_summary || "—"}</div>
                      </div>
                      <div className="col-span-2 flex items-center justify-between flex-wrap gap-1">
                        <span className="text-xs text-stone-500">
                          {reqAnalysis.doctor_name
                            ? `Médico: ${reqAnalysis.doctor_name}${
                                reqAnalysis.crm ? ` · CRM ${reqAnalysis.crm}` : ""
                              }`
                            : "Médico não identificado"}
                        </span>
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${
                            reqAnalysis.is_medical_document
                              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                              : "bg-rose-50 text-rose-800 border-rose-200"
                          }`}
                        >
                          {reqAnalysis.is_medical_document
                            ? `Documento médico · ${Math.round((reqAnalysis.confidence || 0) * 100)}%`
                            : "Não parece documento médico"}
                        </Badge>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-amber-800 font-body" data-testid="ai-analysis-msg">
                      {analyzeMsg || "Análise indisponível."}
                    </div>
                  )}
                </div>
              )}

              {reqDetail.resolution_note && (
                <div className="text-xs text-stone-500 font-body">
                  Nota do RH: {reqDetail.resolution_note}
                </div>
              )}

              {reqDetail.status === "pending" && (
                <DialogFooter className="gap-2">
                  <Button
                    variant="outline"
                    className="bg-white text-rose-700 border-rose-200 hover:bg-rose-50"
                    onClick={() => {
                      resolveRequest(reqDetail, "reject");
                      setReqDetail(null);
                    }}
                    data-testid="request-detail-reject"
                  >
                    <XCircle className="h-4 w-4 mr-1" /> Recusar
                  </Button>
                  <Button
                    className="bg-emerald-800 hover:bg-emerald-900 text-white"
                    onClick={() => {
                      resolveRequest(reqDetail, "approve");
                      setReqDetail(null);
                    }}
                    data-testid="request-detail-approve"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Aprovar
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule dialog */}
      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingSched ? `Editar jornada — ${editingSched.name}` : "Nova regra de jornada"}
            </DialogTitle>
            <DialogDescription className="font-body text-xs">
              Horários, tolerância, política de extras e colaboradores vinculados.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitSched} className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input
                value={schedForm.name}
                onChange={(e) => setS("name", e.target.value)}
                required
                placeholder="Ex: Turno da manhã"
                className="mt-1 bg-white"
                data-testid="schedule-name-input"
              />
            </div>
            <div>
              <Label>Unidade</Label>
              <Select value={schedForm.unit_id || "all"} onValueChange={(v) => setS("unit_id", v)}>
                <SelectTrigger className="mt-1 bg-white" data-testid="schedule-unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as unidades</SelectItem>
                  {units.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Entrada</Label>
                <Input type="time" value={schedForm.entry_time} onChange={(e) => setS("entry_time", e.target.value)} className="mt-1 bg-white" data-testid="schedule-entry-input" />
              </div>
              <div>
                <Label>Saída intervalo</Label>
                <Input type="time" value={schedForm.break_start} onChange={(e) => setS("break_start", e.target.value)} className="mt-1 bg-white" data-testid="schedule-break-start-input" />
              </div>
              <div>
                <Label>Volta intervalo</Label>
                <Input type="time" value={schedForm.break_end} onChange={(e) => setS("break_end", e.target.value)} className="mt-1 bg-white" data-testid="schedule-break-end-input" />
              </div>
              <div>
                <Label>Saída</Label>
                <Input type="time" value={schedForm.exit_time} onChange={(e) => setS("exit_time", e.target.value)} className="mt-1 bg-white" data-testid="schedule-exit-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tolerância de atraso (min)</Label>
                <Input
                  type="number"
                  min={0}
                  max={60}
                  value={schedForm.tolerance_minutes}
                  onChange={(e) => setS("tolerance_minutes", e.target.value)}
                  className="mt-1 bg-white"
                  data-testid="schedule-tolerance-input"
                />
              </div>
              <div>
                <Label>Destino das horas extras</Label>
                <Select
                  value={schedForm.overtime_policy}
                  onValueChange={(v) => setS("overtime_policy", v)}
                >
                  <SelectTrigger className="mt-1 bg-white" data-testid="schedule-policy-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">Banco de horas</SelectItem>
                    <SelectItem value="payroll">Pagamento (folha)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Dias de trabalho</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="schedule-workdays">
                {WEEKDAYS.map((d, i) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleWorkday(i)}
                    className={`px-2.5 py-1 rounded-full text-xs font-body border transition-colors ${
                      schedForm.workdays.includes(i)
                        ? "bg-emerald-800 text-white border-emerald-800"
                        : "bg-white text-stone-600 border-stone-300"
                    }`}
                    data-testid={`schedule-workday-${i}`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div className="border border-stone-200 rounded-md p-3 bg-stone-50/40">
              <Label className="font-body text-sm font-semibold">Colaboradores desta jornada</Label>
              <div className="space-y-0.5 mt-1.5 max-h-44 overflow-y-auto" data-testid="schedule-users-picker">
                {users.map((u) => (
                  <label
                    key={u.id}
                    className="flex items-center gap-2 text-sm font-body cursor-pointer hover:bg-white px-2 py-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={schedForm.user_ids.includes(u.id)}
                      onChange={() => toggleUser(u.id)}
                      className="accent-emerald-700"
                      data-testid={`schedule-user-${u.id}`}
                    />
                    {u.name}
                    <span className="text-[10px] text-stone-400">({u.email})</span>
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="schedule-save-btn">
                {editingSched ? "Salvar alterações" : "Criar jornada"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
